import type { TabelaFrete } from './frete.js';

/**
 * Custo dos Envios do Mercado Livre — tabela de reputação VERDE.
 *
 * ⚠️ **Procedência, e por que ela importa aqui mais que em qualquer outro lugar
 * deste sistema:**
 *
 * Este número não veio da API. A página oficial bloqueia acesso automatizado, e
 * a tabela foi cruzada dos artigos de Ajuda 40538 / 40545 / 40547, vigente
 * desde 02/03/2026 e conferida em 22/07/2026. É dado de SEGUNDA MÃO com data.
 *
 * Consequência prática: quando o Mercado Livre reajustar, nada aqui vai
 * reclamar. O sistema seguirá calculando com o número velho, e o resultado
 * continuará parecendo conferido. Por isso a data viaja junto com a tabela e
 * aparece na tela — é a única coisa que faz alguém desconfiar na hora certa.
 *
 * Linhas = faixa de peso considerado. Colunas = faixa de preço do anúncio.
 * Tudo em CENTAVOS inteiros, como todo dinheiro neste sistema.
 */

/** Limite superior de cada faixa de peso, em gramas. O último é aberto. */
const PESOS: ReadonlyArray<number | null> = [
  300, 500, 1_000, 1_500, 2_000, 3_000, 4_000, 5_000, 6_000, 7_000, 8_000, 9_000,
  11_000, 13_000, 15_000, 17_000, 20_000, 25_000, 30_000, 40_000, 50_000, 60_000,
  70_000, 80_000, 90_000, 100_000, 125_000, 150_000, null,
];

/** Limite superior de cada faixa de preço, em centavos. O último é aberto. */
const PRECOS: ReadonlyArray<number | null> = [
  1_899, 4_899, 7_899, 9_999, 11_999, 14_999, 19_999, null,
];

/** Uma linha por faixa de peso, na ordem de PESOS. */
const VALORES: ReadonlyArray<ReadonlyArray<number>> = [
  [565, 655, 775, 1_235, 1_435, 1_645, 1_845, 2_095], // até 0,3 kg
  [595, 665, 785, 1_325, 1_545, 1_765, 1_985, 2_255], // 0,3 a 0,5
  [605, 675, 795, 1_385, 1_615, 1_845, 2_075, 2_365], // 0,5 a 1
  [615, 685, 805, 1_415, 1_645, 1_885, 2_115, 2_465], // 1 a 1,5
  [625, 695, 815, 1_445, 1_685, 1_925, 2_165, 2_465], // 1,5 a 2
  [635, 795, 855, 1_575, 1_835, 2_105, 2_365, 2_625], // 2 a 3
  [645, 815, 895, 1_705, 1_985, 2_265, 2_555, 2_835], // 3 a 4
  [655, 835, 975, 1_845, 2_155, 2_465, 2_775, 3_075], // 4 a 5
  [665, 855, 995, 2_545, 2_855, 3_265, 3_575, 3_975], // 5 a 6
  [675, 875, 1_015, 2_705, 3_105, 3_605, 4_005, 4_405], // 6 a 7
  [685, 895, 1_035, 2_885, 3_365, 3_845, 4_325, 4_805], // 7 a 8
  [695, 915, 1_055, 2_965, 3_455, 3_955, 4_445, 4_935], // 8 a 9
  [705, 955, 1_095, 4_125, 4_805, 5_495, 6_175, 6_865], // 9 a 11
  [715, 995, 1_135, 4_215, 4_925, 5_625, 6_325, 7_025], // 11 a 13
  [725, 1_015, 1_155, 4_505, 5_245, 5_995, 6_745, 7_495], // 13 a 15
  [735, 1_035, 1_175, 4_855, 5_605, 6_355, 7_075, 7_865], // 15 a 17
  [745, 1_055, 1_195, 5_475, 6_385, 7_295, 8_205, 9_115], // 17 a 20
  [765, 1_095, 1_215, 6_405, 7_505, 8_475, 9_535, 10_595], // 20 a 25
  [775, 1_115, 1_235, 6_595, 7_545, 8_555, 9_625, 10_695], // 25 a 30
  [785, 1_135, 1_255, 6_775, 7_895, 8_895, 9_915, 10_705], // 30 a 40
  [795, 1_155, 1_275, 7_025, 8_105, 9_205, 10_255, 11_075], // 40 a 50
  [805, 1_175, 1_295, 7_495, 8_645, 9_815, 10_935, 11_815], // 50 a 60
  [815, 1_195, 1_315, 8_025, 9_295, 10_505, 11_715, 12_655], // 60 a 70
  [825, 1_215, 1_335, 8_395, 9_705, 10_985, 12_245, 13_225], // 70 a 80
  [835, 1_235, 1_355, 9_325, 10_745, 12_205, 13_605, 14_695], // 80 a 90
  [845, 1_255, 1_375, 10_655, 12_395, 13_955, 15_555, 16_795], // 90 a 100
  [855, 1_275, 1_395, 11_925, 13_805, 15_605, 17_395, 18_795], // 100 a 125
  [865, 1_275, 1_415, 12_655, 14_615, 16_565, 18_465, 19_945], // 125 a 150
  [875, 1_295, 1_435, 16_615, 19_245, 21_755, 24_255, 26_195], // mais de 150
];

export const TABELA_FRETE_ML: TabelaFrete = {
  faixasPesoGramas: PESOS,
  faixasPrecoCents: PRECOS,
  valoresCents: VALORES,
  fonte: 'Ajuda do Mercado Livre 40538 / 40545 / 40547, cruzada (a página oficial bloqueia acesso automatizado)',
  vigenteDesde: '2026-03-02',
  conferidaEm: '2026-07-22',
};
